"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.kindGovernsClock = kindGovernsClock;
exports.kindGovernsQueryClock = kindGovernsQueryClock;
exports.computeSubjectToSatelliteCaps = computeSubjectToSatelliteCaps;
exports.timeBasisLabel = timeBasisLabel;
exports.computeContainerClock = computeContainerClock;
const date_1 = require("encamp-shared/dist/utils/date");
const episodic_1 = require("./episodic");
const EMPTY_CLOCK = {
    limits: [],
    clockStartDate: null,
    effectiveDeadlineDate: null,
    undetermined: null,
};
// The satellite move-window deadline (area.moveDeadline) is a genuine
// timestamp owned by the area rollup and is deliberately NOT floored here.
// TODO - we should actually remove the timestamp behavior and switch to calendar days
//        confirmed by Jess in 2026-08-18 meeting with waste pod
function resolveClockAnchorDate(container, area, governedByKind) {
    const anchor = resolveRawClockAnchorDate(container, area, governedByKind);
    return anchor === null ? null : (0, date_1.floorToUtcMidnight)(anchor);
}
function resolveRawClockAnchorDate(container, area, governedByKind) {
    if (container.isPermanent) {
        if (container.isFlowThrough) {
            return container.lastEmptiedAt ?? container.accumulationStartDate;
        }
        // No fallback: null means no ADDs in the current cycle, so an empty tank
        // has no clock. The caller's deriver returns the first ADD when the tank
        // has never been emptied.
        return container.oldestAddAfterLastEmpty;
    }
    // The central-move substitution below is a 40 CFR part 262 concept: the
    // 90/180/270-day clock starts when waste arrives in central storage. A
    // kind program's clock instead runs from when the waste started
    // accumulating on site and does not restart on an internal move, so a
    // container the kind actually governs anchors to its accumulation start.
    // `governedByKind` is the resolved answer rather than a kind check, because
    // carrying a kind is not the same as being governed by one — see
    // `kindGovernsClock`.
    if (area?.type === "CENTRAL" && !governedByKind) {
        // A container onboarded directly into central has no move stamp; its
        // accumulation start is the date it entered central.
        return container.movedToCentralDate ?? container.accumulationStartDate;
    }
    return container.accumulationStartDate;
}
function buildRuleQuery(container, areaType, facility) {
    return {
        state: facility.state,
        generatorStatus: facility.generatorStatus,
        areaType,
        mustTransport200Miles: container.sqgOver200Miles,
        stateGeneratorStatus: facility.stateGeneratorStatus,
        classification: container.classification,
        wasteKind: container.wasteKind ?? null,
        operatesUnderSubpartP: facility.operatesUnderSubpartP,
        managedAsUniversalWaste: container.managedAsUniversalWaste,
    };
}
/** Same rule, not merely the same answer: two rows can agree on a number. */
function sameClock(a, b) {
    if (a.kind !== b.kind) {
        return false;
    }
    if (a.kind === "TIME" && b.kind === "TIME") {
        return a.days === b.days && a.citation === b.citation;
    }
    if (a.kind === "NO_LIMIT" && b.kind === "NO_LIMIT") {
        return a.citation === b.citation;
    }
    // Both UNDETERMINED: no citation to distinguish, and neither is a kind
    // program's answer.
    return true;
}
/**
 * Whether a kind-specific program actually answers this container's clock, as
 * opposed to the container merely carrying a kind. Resolved by asking the
 * ruleset twice — once with the container's kind, once as ordinary hazardous
 * waste — and comparing the rule that answered.
 *
 * That comparison IS the question, and it stays right where a kind check goes
 * wrong. California restates its ordinary chapter 12 limits for used oil (a
 * non-RCRA state hazardous waste there) and for pharmaceutical waste (it never
 * adopted Subpart P), and the federal set restates part 262 for
 * pharmaceuticals outside Subpart P. In all three the container carries a kind
 * that governs nothing, and reading the kind alone would attribute the
 * ordinary answer to the kind's program: an anchor that skips the central-move
 * substitution, a basis string naming the wrong rulebook, and a
 * status-resolved flag that hides the missing-status callout on a clock that
 * genuinely depends on generator status.
 *
 * Exported so the read path asks the engine instead of re-deriving the
 * predicate — the duplication this module's persisted-flag work exists to
 * remove.
 */
function kindGovernsClock(container, areaType, facility, rules) {
    return kindGovernsQueryClock(buildRuleQuery(container, areaType, facility), rules);
}
/**
 * The same question for a caller that holds a `RuleQuery` rather than a
 * container — the facility-level ruleset field, which invents the kind
 * instead of reading one off a stream. Both callers must reach the same
 * answer, so the comparison lives here once rather than beside each of them.
 */
function kindGovernsQueryClock(query, rules) {
    if (query.wasteKind === null) {
        return false;
    }
    return !sameClock(rules(query).clock, rules({ ...query, wasteKind: null }).clock);
}
/**
 * Whether the satellite volume caps reach this container — resolved
 * independently of the container's CURRENT area, so the answer is stable
 * across area moves: a central container carries the value it would have if
 * it were sitting in a satellite area right now. Persisted by
 * `persistContainerClock` onto `WasteContainer.subjectToSatelliteCaps`, the
 * single source the clock-bucket SQL reads instead of re-deriving the VSQG
 * carve-out, the kind exemptions, and the Subpart P gate itself.
 *
 * A sibling of `computeContainerClock` rather than a field on its result:
 * the flag is a (kind, facility, classification) fact, not a function of the
 * container's current area or lifecycle status, so it stays defined even for
 * a SHIPPED or unplaced container that `computeContainerClock` short-circuits
 * on.
 */
function computeSubjectToSatelliteCaps(container, facility, rules) {
    const resolved = rules(buildRuleQuery(container, "SATELLITE", facility));
    return resolved.areaType === "SATELLITE" && resolved.caps.kind === "VOLUME";
}
const WASTE_KIND_BASIS_LABELS = {
    UNIVERSAL_WASTE: "universal waste",
    USED_OIL: "used oil",
    PHARMACEUTICAL: "pharmaceutical waste",
};
// Matches the retired rule records' basis strings: federal central rules were
// written per generator tier; state rules decide the tier internally, which
// the records rendered as "any generator". A clock a kind program answered
// runs off that program rather than a generator tier, so it names the kind
// instead. Gated on `governedByKind`, not on the kind being present: a
// container whose kind governs nothing took its number from the ordinary
// rules and must not name the kind's rulebook.
function timeBasisLabel(limit, query, governedByKind) {
    if (governedByKind && query.wasteKind !== null) {
        return WASTE_KIND_BASIS_LABELS[query.wasteKind];
    }
    if (limit.jurisdiction === "FEDERAL" && query.generatorStatus !== null) {
        return query.generatorStatus;
    }
    return "any generator";
}
function timeBasis(limit, query, governedByKind) {
    return `${limit.days} days — ${timeBasisLabel(limit, query, governedByKind)}, ${query.areaType.toLowerCase()}`;
}
function buildVolumeLimit(caps) {
    return {
        limitType: "VOLUME",
        basis: `SAA ${caps.nonAcuteGal} gal / ${caps.acuteGal} gal (${caps.acuteKg} kg) acute`,
        citation: caps.citation,
        jurisdiction: caps.jurisdiction,
        deadlineDate: null,
        totalDays: null,
        isMoveWindow: false,
    };
}
function buildMoveWindowLimit(caps, moveDeadline) {
    return {
        limitType: "TIME",
        basis: `SAA ${caps.moveWindowDays}-day move window`,
        citation: caps.citation,
        jurisdiction: caps.jurisdiction,
        deadlineDate: moveDeadline,
        totalDays: caps.moveWindowDays,
        isMoveWindow: true,
    };
}
function buildTimeLimit(limit, query, anchor, governedByKind) {
    return {
        limitType: "TIME",
        basis: timeBasis(limit, query, governedByKind),
        citation: limit.citation,
        jurisdiction: limit.jurisdiction,
        deadlineDate: (0, date_1.addUtcDays)(anchor, limit.days),
        totalDays: limit.days,
        isMoveWindow: false,
    };
}
/** The competing residence clock whose deadline binds. A missing deadline
 * (never true of the two callers' limits, both built with one) yields to the
 * other; ties keep `a`, so the caller decides the tiebreak by argument order. */
function soonerTimeLimit(a, b) {
    if (a.deadlineDate === null) {
        return b;
    }
    if (b.deadlineDate === null) {
        return a;
    }
    return b.deadlineDate.getTime() < a.deadlineDate.getTime() ? b : a;
}
// Labelled by the event rather than by the generator tier: "60 days — VSQG,
// central" would name the facility's standing category instead of the reason
// this window exists at all.
function buildEpisodicLimit(episodic) {
    return {
        limitType: "TIME",
        basis: `${episodic.limit.days} days — episodic event`,
        citation: episodic.limit.citation,
        jurisdiction: episodic.limit.jurisdiction,
        deadlineDate: (0, date_1.addUtcDays)(episodic.anchorDate, episodic.limit.days),
        totalDays: episodic.limit.days,
        isMoveWindow: false,
    };
}
function computeContainerClock(container, area, facility, rules) {
    if (container.status === "SHIPPED" || container.archivedAt !== null || area === null) {
        return EMPTY_CLOCK;
    }
    // Resolved before the anchor because the anchor depends on it: whether the
    // central-move substitution applies turns on which rulebook answered.
    const governedByKind = kindGovernsClock(container, area.type, facility, rules);
    const anchor = resolveClockAnchorDate(container, area, governedByKind);
    const query = buildRuleQuery(container, area.type, facility);
    const resolved = rules(query);
    const limits = [];
    if (resolved.areaType === "SATELLITE" && resolved.caps.kind === "VOLUME") {
        limits.push(buildVolumeLimit(resolved.caps));
        if (area.isOverThreshold && area.moveDeadline !== null) {
            limits.push(buildMoveWindowLimit(resolved.caps, area.moveDeadline));
        }
    }
    // The anchor, not the generator status, gates the deadline: a state's
    // standing satellite clock (CA's one-year limit) resolves even with no
    // resolvable status, while a null anchor means no ADDs in the current
    // cycle — nothing to date a deadline from.
    const standing = resolved.clock.kind === "TIME" && anchor !== null
        ? buildTimeLimit(resolved.clock, query, anchor, governedByKind)
        : null;
    // The episodic overlay, resolved independently of the jurisdiction. It
    // carries its own anchor (the EVENT's start), so it exists even where the
    // container has no anchor of its own — the VSQG case, whose central
    // containers otherwise have no standing clock to hang a deadline on.
    //
    // Withheld where a kind program answers the clock. Subpart L is part 262,
    // and a container part 262 does not reach — universal waste is excluded from
    // it outright (40 CFR 261.9, 273.1(b)) — takes no 60-day window from an
    // event. Attaching one is a data-entry mistake rather than a clock question,
    // and the fix for it is to leave the kind's own limit standing.
    const episodic = governedByKind
        ? null
        : (0, episodic_1.episodicLimit)({
            state: facility.state,
            generatorStatus: facility.generatorStatus,
            areaType: area.type,
            activeEpisodicEvent: container.activeEpisodicEvent,
        });
    // The standing clock and the episodic overlay compete for one obligation —
    // how long this waste may stay — so only the one whose deadline binds is
    // emitted: `limits` carries what APPLIES, and a superseded residence clock
    // applies to nothing. Taking the earlier is the whole of "episodic
    // supersedes the standing clock" without ever EXTENDING a deadline — the
    // reading the attach guard exists to prevent. Everything else in `limits`
    // (the volume cap, its move window) is a separate, coinciding obligation.
    if (standing !== null && episodic !== null) {
        limits.push(soonerTimeLimit(buildEpisodicLimit(episodic), standing));
    }
    else if (standing !== null) {
        limits.push(standing);
    }
    else if (episodic !== null) {
        limits.push(buildEpisodicLimit(episodic));
    }
    const timeDeadlines = limits
        .map((limit) => limit.deadlineDate)
        .filter((deadline) => deadline !== null);
    const effectiveDeadlineDate = timeDeadlines.length > 0
        ? new Date(Math.min(...timeDeadlines.map((deadline) => deadline.getTime())))
        : null;
    // The rules' own UNDETERMINED, passed through with the payload the
    // jurisdiction composed at the point it decided it couldn't answer. The
    // payload is required on every imperative return; the fallbacks only
    // satisfy the type's expectation-row looseness (see Undetermined).
    const undetermined = resolved.clock.kind === "UNDETERMINED"
        ? { missingAxes: resolved.clock.missingAxes ?? [], reason: resolved.clock.reason ?? "" }
        : null;
    return {
        limits,
        clockStartDate: anchor,
        effectiveDeadlineDate,
        undetermined,
    };
}
//# sourceMappingURL=computeContainerClock.js.map